import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Brand";
import { LayoutDashboard, Building2, ClipboardList, Users, CreditCard, LogOut } from "lucide-react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/properties", label: "Properties", icon: Building2, testid: "nav-properties" },
  { to: "/app/inspections", label: "Inspections", icon: ClipboardList, testid: "nav-inspections" },
  { to: "/app/team", label: "Team", icon: Users, testid: "nav-team" },
  { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
];

export default function Layout({ children }) {
  const { user, org, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const active = (to) => (to === "/app" ? loc.pathname === "/app" : loc.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <Link to="/app" className="flex items-center gap-3">
            <Logo className="h-11" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
              <div className="text-xs uppercase tracking-wide text-blue-600">{user?.role} · {org?.name}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); nav("/"); }}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-6">
        <aside className="hidden w-56 shrink-0 lg:block">
          <nav className="sticky top-24 space-y-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                  active(n.to) ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </Link>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 pb-24 lg:pb-6">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-5 border-t border-slate-200 bg-white lg:hidden">
        {NAV.map((n) => (
          <Link
            key={n.to}
            to={n.to}
            data-testid={`${n.testid}-mobile`}
            className={`flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold uppercase tracking-wide ${
              active(n.to) ? "text-blue-600" : "text-slate-500"
            }`}
          >
            <n.icon className="h-5 w-5" />
            {n.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
